import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { Event, Outfit } from '@/types';

export interface PdfTheme {
  primary: string;
  secondary: string;
  accent: string;
}

export const PDF_THEMES: { name: string; theme: PdfTheme }[] = [
  { name: 'Rose', theme: { primary: '#C2185B', secondary: '#FCE4EC', accent: '#F48FB1' } },
  { name: 'Gold', theme: { primary: '#B8860B', secondary: '#FFF8E1', accent: '#FFD54F' } },
  { name: 'Navy', theme: { primary: '#1A237E', secondary: '#E8EAF6', accent: '#7986CB' } },
  { name: 'Sage', theme: { primary: '#2E7D32', secondary: '#E8F5E9', accent: '#81C784' } },
  { name: 'Noir', theme: { primary: '#212121', secondary: '#F5F5F5', accent: '#9E9E9E' } },
];

interface OutfitReportLayoutProps {
  event: Event;
  outfits: Outfit[];
  theme?: PdfTheme;
}

/** Fixed render width used for the preview layout */
export const REPORT_WIDTH = 800;

export const OutfitReportLayout = forwardRef<HTMLDivElement, OutfitReportLayoutProps>(
  ({ event, outfits, theme }, ref) => {
    const { t } = useTranslation();
    const c = theme || PDF_THEMES[0].theme;

    const renderCategory = (label: string, notes?: string, images?: string[]) => {
      if (!notes && (!images || images.length === 0)) return null;
      return (
        <div style={{ marginBottom: 10 }}>
          {/* Label */}
          <div
            style={{
              fontSize: 9,
              textTransform: 'uppercase' as const,
              letterSpacing: '0.14em',
              fontWeight: 700,
              color: c.primary,
              marginBottom: 5,
            }}
          >
            {t(label)}
          </div>

          {/* Thumbnails row */}
          {images && images.length > 0 && (
            <div style={{ display: 'flex', gap: 6 }}>
              {images.map((url, i) => (
                <div
                  key={i}
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 6,
                    overflow: 'hidden',
                    backgroundColor: '#f0f0f0',
                    border: `1px solid ${c.secondary}`,
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={url}
                    crossOrigin="anonymous"
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Notes (truncated) */}
          {notes && (
            <p
              style={{
                fontSize: 9,
                color: '#777',
                fontStyle: 'italic',
                lineHeight: 1.3,
                marginTop: 3,
              }}
            >
              {notes.length > 80 ? notes.slice(0, 80) + '…' : notes}
            </p>
          )}
        </div>
      );
    };

    return (
      <div ref={ref} className="print-container" style={{ width: REPORT_WIDTH, margin: '0 auto' }}>
        {/* ── Cover page ── */}
        <div
          data-pdf-page="cover"
          style={{
            width: REPORT_WIDTH,
            padding: '60px 48px 48px',
            textAlign: 'center' as const,
            background: `linear-gradient(180deg, ${c.secondary} 0%, #fff 100%)`,
            fontFamily: "'Nunito', sans-serif",
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 12,
              background: c.primary,
              margin: '0 auto 24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: 20,
              lineHeight: 1,
            }}
          >
            ✦
          </div>
          <h1
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 36,
              fontWeight: 700,
              fontStyle: 'italic',
              color: c.primary,
              margin: '0 0 10px',
              lineHeight: 1.15,
            }}
          >
            {event.name}
          </h1>
          <p
            style={{
              fontSize: 11,
              textTransform: 'uppercase' as const,
              letterSpacing: '0.2em',
              fontWeight: 700,
              color: c.accent,
            }}
          >
            {t('export.fashionPlan', 'Fashion Plan')} &bull; {event.total_days}{' '}
            {t('outfit.day', 'Days')}
          </p>

          {/* Sponsor attribution */}
          <div style={{ marginTop: 36, opacity: 0.7 }}>
            <p style={{ fontSize: 8, color: '#aaa', marginBottom: 2 }}>Sponsored by</p>
            <a
              href="https://beddamatri.shop/"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: c.primary,
                textDecoration: 'none',
              }}
            >
              Bedda Matri
            </a>
            <p style={{ fontSize: 7, color: '#bbb', fontStyle: 'italic', marginTop: 2 }}>
              beddamatri.shop
            </p>
          </div>
        </div>

        {/* ── Day pages — one per outfit ── */}
        {outfits.map((outfit) => {
          const cats = [
            { label: 'outfit.title', notes: outfit.outfit, images: outfit.outfit_images },
            { label: 'outfit.hair', notes: outfit.hair, images: outfit.hair_images },
            { label: 'outfit.jewelry', notes: outfit.jewelry, images: outfit.jewelry_images },
            { label: 'outfit.purse', notes: outfit.purse, images: outfit.purse_images },
            { label: 'outfit.shoes', notes: outfit.shoes, images: outfit.shoes_images },
          ];

          const hasAnything =
            cats.some((cat) => (cat.images?.length || 0) > 0 || !!cat.notes) || !!outfit.notes;

          if (!hasAnything) return null;

          return (
            <div
              key={outfit.id}
              data-pdf-page={`day-${outfit.day_number}`}
              style={{
                width: REPORT_WIDTH,
                padding: '24px 32px 20px',
                backgroundColor: '#fff',
                fontFamily: "'Nunito', sans-serif",
                borderTop: `3px solid ${c.accent}`,
              }}
            >
              {/* Day header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  marginBottom: 16,
                  paddingBottom: 8,
                  borderBottom: `1px solid ${c.secondary}`,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <span
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontSize: 24,
                      fontWeight: 700,
                      color: c.primary,
                    }}
                  >
                    {t('outfit.day')} {outfit.day_number}
                  </span>
                  <span style={{ fontSize: 10, color: '#bbb', fontWeight: 600 }}>
                    / {event.total_days}
                  </span>
                </div>
                <span
                  style={{
                    fontSize: 8,
                    color: c.accent,
                    fontWeight: 700,
                    textTransform: 'uppercase' as const,
                    letterSpacing: '0.12em',
                  }}
                >
                  {event.name}
                </span>
              </div>

              {/* All categories — single column, stacked */}
              {cats.map((cat, i) => (
                <div key={i}>{renderCategory(cat.label, cat.notes, cat.images)}</div>
              ))}

              {/* General notes */}
              {outfit.notes && (
                <div
                  style={{
                    marginTop: 8,
                    padding: '8px 12px',
                    backgroundColor: c.secondary,
                    borderRadius: 6,
                    fontSize: 9,
                    color: '#555',
                    lineHeight: 1.4,
                    fontStyle: 'italic',
                  }}
                >
                  {outfit.notes}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  },
);

OutfitReportLayout.displayName = 'OutfitReportLayout';
